import React, { useState } from 'react';
import {
  BeneficiaryType,
  Beneficiaries,
  Beneficiary,
  BeneficiaryPercentage
} from '../utils/beneficiaryInterfaces';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import styled from 'styled-components';
import BorderedButton from '../BorderedButton';
import { MainCategoryProps } from '../MainCategory';
import PercentageFieldWithLabel from '../PercentageFieldWithLabel';
import { setBeneficiaryValue } from '../utils/helperFunctions';
import { BeneficiaryValuesBox } from './BeneficiaryMultiValues';

interface BeneficiarySingleValueProps<T extends BeneficiaryType> {
  buttonMinWidth?: number;
  title: string;
  errorMessage?: string;
  buttonLabel: string;
  inputLabel: string;
  categoryName: keyof Beneficiaries;
  category: Beneficiary<T>[] | undefined;
  fieldName: T;
  setBeneficiaries: MainCategoryProps['setBeneficiaries'];
}

const BeneficiarySingleValueContainer = <T extends BeneficiaryType>({
  title,
  errorMessage,
  buttonLabel,
  inputLabel,
  category,
  categoryName,
  fieldName,
  buttonMinWidth,
  setBeneficiaries
}: BeneficiarySingleValueProps<T>) => {
  const [initialValue] = useState<BeneficiaryPercentage>(
    category?.find(x => x.name === fieldName)?.percentage || null
  );
  const [open, setOpen] = useState<boolean>(
    category?.findIndex(x => x.name === fieldName) !== -1
  );
  const handleSave = (percentage: BeneficiaryPercentage) => {
    setBeneficiaries(s => ({
      ...s,
      [categoryName]: {
        categories: setBeneficiaryValue(
          s[categoryName]?.categories as Beneficiary<T>[],
          fieldName,
          percentage
        )
      }
    }));
  };
  const handleOpen = () => setOpen(true);
  if (!open)
    return (
      <BorderedButton
        minWidth={buttonMinWidth}
        text={buttonLabel}
        onClick={handleOpen}
      />
    );
  return (
    <BeneficiaryValuesBox>
      <div className="header">{title}</div>
      {errorMessage && (
        <TrueImpactText variant="body-small" className="error">
          {errorMessage}
        </TrueImpactText>
      )}
      <div className="content-wrapper">
        <PercentageFieldWithLabel
          label={inputLabel}
          focusOnMount={true}
          initialValue={initialValue}
          onBlur={handleSave}
          disabled={false}
        />
      </div>
    </BeneficiaryValuesBox>
  );
};

const StyledDiv = styled.div`
  margin-bottom: 24px;
`;

const BeneficiarySingleValue = <T extends BeneficiaryType>(
  props: BeneficiarySingleValueProps<T>
) => (
  <StyledDiv>
    <BeneficiarySingleValueContainer {...props} />
  </StyledDiv>
);

export default BeneficiarySingleValue;
